import React, { useState, useEffect } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import AppLayout from "../Layout/AppLayout";

function formatDollars(cents) {
  return (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function PartnerEarnings({
  earnings,
  loading,
  persona,
  userName,
  userInitials,
  planProgress,
  actionPlanBadge,
  isPartner,
}) {
  const [expandedMonth, setExpandedMonth] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.ahoy) {
      window.ahoy.track("succeedo_partner_billing_viewed");
    }
  }, []);

  const toggleMonth = (month) => {
    setExpandedMonth(expandedMonth === month ? null : month);
  };

  const totalEarned = earnings ? earnings.total_earned : 0;
  const thisMonth = earnings ? earnings.this_month : 0;
  const pending = earnings ? earnings.pending : 0;
  const months = earnings ? earnings.months : [];

  if (loading) {
    return (
      <AppLayout
        breadcrumb="PARTNER BILLING"
        persona={persona}
        userName={userName}
        userInitials={userInitials}
        isPartner={isPartner}
        planProgress={planProgress}
        actionPlanBadge={actionPlanBadge}
      >
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-[12px] p-5 animate-pulse"
              >
                <div className="h-3 w-24 bg-muted rounded mb-4" />
                <div className="h-8 w-32 bg-muted rounded" />
              </div>
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      breadcrumb="PARTNER BILLING"
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      isPartner={isPartner}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="p-6">
        {/* Summary Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-[12px] p-5">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-2">
              TOTAL FEES
            </div>
            <div className="text-[32px] font-light text-foreground">
              ${formatDollars(totalEarned)}
            </div>
          </div>
          <div className="bg-card border border-border rounded-[12px] p-5">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-2">
              THIS MONTH
            </div>
            <div className="text-[32px] font-light text-foreground">
              ${formatDollars(thisMonth)}
            </div>
          </div>
          <div className="bg-card border border-border rounded-[12px] p-5">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-2">
              OUTSTANDING
            </div>
            <div className="text-[32px] font-light text-foreground">
              ${formatDollars(pending)}
            </div>
          </div>
        </div>

        {/* Section Label */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/40">
            MONTHLY BREAKDOWN
          </span>
        </div>

        {/* Monthly Breakdown Table */}
        <div className="bg-card border border-border rounded-[16px] overflow-hidden">
          {/* Table Header */}
          <div className="bg-muted/50 px-5 py-3 grid grid-cols-12 gap-4 font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide">
            <div className="col-span-4">MONTH</div>
            <div className="col-span-3">LEADS ACCEPTED</div>
            <div className="col-span-3">TOTAL FEES</div>
            <div className="col-span-2">STATUS</div>
          </div>

          {/* Table Rows */}
          {months.map((earning) => (
            <div key={earning.month}>
              <button
                onClick={() => toggleMonth(earning.month)}
                className="w-full px-5 py-4 border-b border-border grid grid-cols-12 gap-4 items-center hover:bg-muted/30 transition-colors text-left"
              >
                <div className="col-span-4 text-[14px] text-foreground flex items-center gap-2">
                  {expandedMonth === earning.month ? (
                    <ChevronUpIcon className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                  )}
                  {earning.month}
                </div>
                <div className="col-span-3 text-[14px] text-foreground">
                  {earning.leads_accepted}
                </div>
                <div className="col-span-3 text-[14px] text-foreground">
                  ${formatDollars(earning.total_fees)}
                </div>
                <div className="col-span-2">
                  <span
                    className={`inline-block rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wide ${
                      earning.status === "paid"
                        ? "bg-green-500/10 text-green-400"
                        : "bg-amber-500/10 text-amber-400"
                    }`}
                  >
                    {earning.status}
                  </span>
                </div>
              </button>

              {/* Expandable Line Items */}
              {expandedMonth === earning.month && (
                <div className="bg-muted/30">
                  {earning.line_items.map((item) => (
                    <a
                      key={item.id}
                      href={`/succeedo/partner/leads/${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/succeedo/partner/leads/${item.id}`;
                      }}
                      className="block px-8 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors"
                    >
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-4 text-[13px] text-foreground">
                          {item.owner_name}
                        </div>
                        <div className="col-span-3 text-[13px] text-muted-foreground">
                          {item.industry}
                        </div>
                        <div className="col-span-3 text-[13px] text-muted-foreground">
                          {item.date_accepted}
                        </div>
                        <div className="col-span-2 text-[13px] text-foreground font-medium">
                          ${formatDollars(item.fee)}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
